/* ============================================================
   BULKD — Build a Bundle ("Build Your Stack")
   Reads product data from each card's data-* attributes (rendered
   live by bundle-builder.liquid). Manages the stack tray, the
   30-day subscription toggle, a count-based reward ladder (tier
   cards + checklist), a roll-the-dice mystery gift, stacking
   savings (subscription 30% + pick-tier 5/10%), the mobile bottom
   sheet (focus-trapped modal), and add-to-cart with selling plans
   + a discount-code redirect.
   ============================================================ */
(function () {
  'use strict';

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var ICON = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L20 7"/></svg>',
    lock:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
    gift:  '<svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.94 9V15.98C14.94 16.23 14.74 16.43 14.49 16.43H3.51C3.26 16.43 3.06 16.23 3.06 15.98V9"/><path d="M15.98 5.29H2.02C1.77 5.29 1.58 5.49 1.58 5.73V8.55C1.58 8.8 1.77 9 2.02 9H15.98C16.23 9 16.43 8.8 16.43 8.55V5.73C16.43 5.49 16.23 5.29 15.98 5.29Z"/><path d="M9 16.43V5.29"/><path d="M9 5.29H5.66C5.17 5.29 4.69 5.09 4.35 4.74C4 4.4 3.8 3.92 3.8 3.43C3.8 2.94 4 2.47 4.35 2.12C4.69 1.77 5.17 1.58 5.66 1.58C8.26 1.58 9 5.29 9 5.29Z"/><path d="M9 5.29H12.34C12.83 5.29 13.31 5.09 13.65 4.74C14 4.4 14.2 3.92 14.2 3.43C14.2 2.94 14 2.47 13.65 2.12C13.31 1.77 12.83 1.58 12.34 1.58C9.74 1.58 9 5.29 9 5.29Z"/></svg>'
  };

  /* ====================== real 3D die — 2D-canvas software renderer (ported from NZ Supps) ======================
   * A cube is convex, so sorting its 6 faces back-to-front and filling them opaque gives EXACT occlusion at
   * every angle. Pips are geometry ON the face plane so they foreshorten WITH the face. */
  var DICE_V = [[-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],[-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1]];
  var DICE_F = [
    { idx:[4,5,6,7], n:[0,0,1],  num:1 },
    { idx:[1,0,3,2], n:[0,0,-1], num:6 },
    { idx:[5,1,2,6], n:[1,0,0],  num:3 },
    { idx:[0,4,7,3], n:[-1,0,0], num:4 },
    { idx:[4,0,1,5], n:[0,-1,0], num:2 },
    { idx:[7,6,2,3], n:[0,1,0],  num:5 }
  ];
  var DICE_PIP = {
    1:[[0,0]],
    2:[[-0.62,-0.62],[0.62,0.62]],
    3:[[-0.62,-0.62],[0,0],[0.62,0.62]],
    4:[[-0.62,-0.62],[0.62,-0.62],[-0.62,0.62],[0.62,0.62]],
    5:[[-0.62,-0.62],[0.62,-0.62],[0,0],[-0.62,0.62],[0.62,0.62]],
    6:[[-0.62,-0.66],[0.62,-0.66],[-0.62,0],[0.62,0],[-0.62,0.66],[0.62,0.66]]
  };
  var DICE_L = (function () { var l=[-0.35,-0.55,0.72], m=Math.hypot(l[0],l[1],l[2]); return [l[0]/m,l[1]/m,l[2]/m]; })();
  var TAU = 6.283185307;

  function diceHtml() { return '<canvas class="bbb-dice-cv" data-dice aria-hidden="true"></canvas>'; }

  function createDiceRenderer(canvas) {
    var dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
    var S = Math.round(180 * dpr);
    canvas.width = S; canvas.height = S;
    var ctx = canvas.getContext('2d');
    var D = 5.2, SC = S * 0.225, CEN = S / 2, LW = 1.7 * dpr, PR = 0.17;
    var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    function rot(p, ax, ay, az) {
      var x=p[0], y=p[1], z=p[2], c, s, t;
      c=Math.cos(ax); s=Math.sin(ax); t=y*c-z*s; z=y*s+z*c; y=t;
      c=Math.cos(ay); s=Math.sin(ay); t=x*c+z*s; z=-x*s+z*c; x=t;
      c=Math.cos(az); s=Math.sin(az); t=x*c-y*s; y=x*s+y*c; x=t;
      return [x, y, z];
    }
    function proj(p) { var f = D / (D - p[2]); return [CEN + p[0]*SC*f, CEN + p[1]*SC*f]; }
    function flw(f, u, v) {
      var i, p0=DICE_V[f.idx[0]], p1=DICE_V[f.idx[1]], p2=DICE_V[f.idx[2]], p3=DICE_V[f.idx[3]], o=[0,0,0];
      for (i=0;i<3;i++) {
        var c = (p0[i]+p1[i]+p2[i]+p3[i]) / 4;
        o[i] = c + (p1[i]-p0[i])/2*u*0.62 + (p3[i]-p0[i])/2*v*0.62;
      }
      return o;
    }
    function draw(ax, ay, az) {
      ctx.clearRect(0,0,S,S);
      var tv = DICE_V.map(function (p) { return rot(p, ax, ay, az); });
      var fs = DICE_F.map(function (f) {
        return { f:f, tn:rot(f.n,ax,ay,az), cz:(tv[f.idx[0]][2]+tv[f.idx[1]][2]+tv[f.idx[2]][2]+tv[f.idx[3]][2])/4 };
      }).sort(function (a,b) { return a.cz - b.cz; });
      for (var k=0;k<fs.length;k++) {
        var f = fs[k].f, tn = fs[k].tn, pts = f.idx.map(function (i) { return proj(tv[i]); });
        var nl = Math.max(0, tn[0]*DICE_L[0] + tn[1]*DICE_L[1] + tn[2]*DICE_L[2]);
        var sh = 0.90 + 0.10*nl, g = Math.round(255*sh), e = Math.round(246*sh), i;
        ctx.beginPath(); ctx.moveTo(pts[0][0],pts[0][1]);
        for (i=1;i<4;i++) ctx.lineTo(pts[i][0],pts[i][1]);
        ctx.closePath();
        var gr = ctx.createLinearGradient(pts[0][0],pts[0][1],pts[2][0],pts[2][1]);
        gr.addColorStop(0,'rgb('+g+','+g+','+g+')'); gr.addColorStop(1,'rgb('+e+','+e+','+e+')');
        ctx.fillStyle = gr; ctx.fill();
        ctx.lineJoin='round'; ctx.lineWidth = LW; ctx.strokeStyle = '#3a2bc4'; ctx.stroke();
        if (tn[2] > 0.02) {
          ctx.fillStyle = '#3a2bc4';
          var pips = DICE_PIP[f.num];
          for (var pi=0; pi<pips.length; pi++) {
            ctx.beginPath();
            for (var a=0; a<22; a++) {
              var th = a/22*TAU;
              var sp = proj(rot(flw(f, pips[pi][0]+PR*Math.cos(th), pips[pi][1]+PR*Math.sin(th)), ax, ay, az));
              if (a===0) ctx.moveTo(sp[0],sp[1]); else ctx.lineTo(sp[0],sp[1]);
            }
            ctx.closePath(); ctx.fill();
          }
        }
      }
    }

    function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
    function easeOut(t) { return 1 - Math.pow(1-t, 4); }

    var REST = { ax:-0.45, ay:0.62, az:0 };
    var raf = 0, destroyed = false, mode = 'idle', visible = true;
    var from = null, to = null, t0 = 0, ROLLDUR = 2300, onEnd = null, io = null;

    function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
    function start() { if (!raf && !destroyed && visible && mode !== 'landed') raf = requestAnimationFrame(loop); }
    function loop() {
      raf = 0;
      if (destroyed) return;
      var t = now();
      if (mode === 'rolling') {
        var p = Math.min(1, (t - t0) / ROLLDUR), k = easeOut(p);
        draw(from.ax+(to.ax-from.ax)*k, from.ay+(to.ay-from.ay)*k, from.az+(to.az-from.az)*k);
        if (p >= 1) {
          mode = 'landed'; REST = { ax:to.ax, ay:to.ay, az:to.az };
          draw(REST.ax, REST.ay, REST.az);
          var cb = onEnd; onEnd = null;
          if (cb) cb();
          return;
        }
      } else {
        draw(REST.ax, REST.ay + Math.sin(t/5200*TAU)*0.16, 0);
      }
      start();
    }

    function init() {
      if (reduce) { mode = 'landed'; draw(REST.ax, REST.ay, REST.az); return; }
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (es) {
          visible = es[0].isIntersecting;
          if (visible) start(); else stop();
        }, { threshold: 0.01 });
        io.observe(canvas);
      }
      mode = 'idle'; draw(REST.ax, REST.ay, 0); start();
    }
    function roll(cb) {
      if (reduce) {
        REST = { ax:-0.5+Math.random()*0.3, ay:Math.random()*TAU, az:(Math.random()-0.5)*0.4 };
        mode = 'landed'; draw(REST.ax, REST.ay, REST.az); if (cb) cb(); return;
      }
      var t = now();
      var curAy = (mode === 'idle') ? (REST.ay + Math.sin(t/5200*TAU)*0.16) : REST.ay;
      from = { ax:REST.ax, ay:curAy, az:REST.az };
      function sign() { return Math.random() < 0.5 ? -1 : 1; }
      function turns() { return (2 + Math.floor(Math.random()*2)) * TAU; }
      to = {
        ax: (-0.62 + Math.random()*0.5) + sign()*turns(),
        ay: (Math.random()*TAU)         + sign()*turns(),
        az: ((Math.random()-0.5)*0.5)   + sign()*(0.6+Math.random()*0.8)*TAU
      };
      onEnd = cb || null; t0 = t; mode = 'rolling'; start();
    }
    function destroy() { destroyed = true; stop(); if (io) { io.disconnect(); io = null; } }

    return { init:init, roll:roll, destroy:destroy };
  }

  function init(root) {
    var SUB_PCT = parseFloat(root.getAttribute('data-sub-pct')) || 30;
    var TIER2_PCT = parseFloat(root.getAttribute('data-tier2-pct')) || 0;
    var TIER4_PCT = parseFloat(root.getAttribute('data-tier4-pct')) || 0;
    var GIFT_UNLOCK = parseInt(root.getAttribute('data-gift-unlock'), 10) || 3;
    var CODE_TIER2 = root.getAttribute('data-code-tier2') || '';
    var CODE_TIER4 = root.getAttribute('data-code-tier4') || '';
    var mode = root.getAttribute('data-sub-default') === '1' ? 'sub' : 'once';
    var DESKTOP = window.matchMedia('(min-width: 960px)');

    /* mystery pool */
    var MYSTERY = [];
    var poolEl = root.querySelector('[data-mystery-pool]');
    if (poolEl) { try { MYSTERY = JSON.parse(poolEl.textContent) || []; } catch (e) { MYSTERY = []; } }

    var cards = [].slice.call(root.querySelectorAll('[data-card]'));
    var main = root.querySelector('.bbb__main');
    var emptyGrid = root.querySelector('[data-empty-grid]');
    var chips = [].slice.call(root.querySelectorAll('.bbb__chip'));
    var itemsEl = root.querySelector('[data-items]');
    var emptyEl = root.querySelector('[data-empty]');
    var countEl = root.querySelector('[data-count]');
    var statusEl = root.querySelector('[data-status]');
    var rewardsEl = root.querySelector('[data-rewards]');
    var ladderRow = root.querySelector('[data-ladder-row]');
    var mysteryEl = root.querySelector('[data-mystery]');
    var subtotalEl = root.querySelector('[data-subtotal]');
    var subsaveRow = root.querySelector('[data-subsave-row]');
    var subsaveLabel = root.querySelector('[data-subsave-label]');
    var subsaveEl = root.querySelector('[data-subsave]');
    var stacksaveRow = root.querySelector('[data-stacksave-row]');
    var stacksaveLabel = root.querySelector('[data-stacksave-label]');
    var stacksaveEl = root.querySelector('[data-stacksave]');
    var totalsaveRow = root.querySelector('[data-totalsave-row]');
    var totalsaveEl = root.querySelector('[data-totalsave]');
    var totalEl = root.querySelector('[data-total]');
    var ctaEl = root.querySelector('[data-cta]');
    var errorEl = root.querySelector('[data-cart-error]');
    var giftNudge = root.querySelector('[data-gift-nudge]');
    var giftNudgeMsg = root.querySelector('[data-gift-nudge-msg]');
    var giftRollBtn = root.querySelector('[data-gift-roll]');
    var giftSkipBtn = root.querySelector('[data-gift-skip]');
    var modeBtns = [].slice.call(root.querySelectorAll('.bbb-sum__opt'));
    var radiogroup = root.querySelector('.bbb-sum__toggle');
    var summary = root.querySelector('[data-summary]');
    var scrim = root.querySelector('[data-scrim]');
    var mobileBar = root.querySelector('[data-mobilebar]');
    var barCount = root.querySelector('[data-bar-count]');
    var barTotal = root.querySelector('[data-bar-total]');
    var barOpen = root.querySelector('[data-bar-open]');
    var sheetClose = root.querySelector('[data-sheet-close]');

    /* currency prefix taken from a card's formatted price ("$" or "NZ$") */
    var PREFIX = '$';
    for (var i = 0; i < cards.length; i++) {
      var f = cards[i].getAttribute('data-price-fmt') || '';
      var m = f.match(/^[^\d.,]+/);
      if (m && m[0].trim()) { PREFIX = m[0].trim(); break; }
    }
    function money(cents) { return PREFIX + ((Number(cents) || 0) / 100).toFixed(2); }

    /* stack state keyed by handle */
    var stack = {};
    var prevKeys = {};         // summary line keys present last render → animate only new lines
    /* mystery state */
    var mystery = null;        // { id, title, brand, price, image }
    var rolling = false;
    var diceR = null;
    var lastMysteryState = '';
    var giftAck = false;       // set true once the shopper has chosen to skip the unclaimed gift

    function cardData(card) {
      return {
        card: card,
        handle: card.getAttribute('data-handle'),
        id: card.getAttribute('data-id'),
        sp: card.getAttribute('data-sp') || '',
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        title: card.getAttribute('data-title') || '',
        img: card.getAttribute('data-img') || '',
        url: card.getAttribute('data-url') || '#'
      };
    }

    function setCardState(card, on) {
      var add = card.querySelector('[data-add]');
      if (on) {
        card.classList.add('is-instack');
        if (add) add.textContent = 'Added';
      } else {
        card.classList.remove('is-instack');
        if (add) add.textContent = 'Add to stack';
      }
    }

    /* one of each — a "pick" is a product in the stack (qty 1), toggled on/off */
    function toggleItem(handle, card) {
      if (stack[handle]) {
        var c = stack[handle].card;
        delete stack[handle];
        setCardState(c, false);
      } else {
        if (!card || !card.getAttribute('data-id')) return; /* never stack an unpurchasable card */
        stack[handle] = cardData(card);
        stack[handle].qty = 1;
        setCardState(card, true);
      }
      render();
    }
    function removeItem(handle) {
      if (!stack[handle]) return;
      var c = stack[handle].card;
      delete stack[handle];
      setCardState(c, false);
      render();
    }

    /* ---- tiers / savings ---- */
    function pickCount() { return Object.keys(stack).length; }
    function stackPct(n) {
      if (n >= 4) return TIER4_PCT;
      if (n >= 2) return TIER2_PCT;
      return 0;
    }
    /* drop the gift if the shopper falls below the unlock tier */
    function enforceTiers() {
      if (pickCount() < GIFT_UNLOCK) mystery = null;
    }

    function totals() {
      var subtotal = 0, subEligible = 0, count = 0, lines = 0, h;
      for (h in stack) {
        if (!stack.hasOwnProperty(h)) continue;
        var lineTotal = stack[h].price * stack[h].qty;
        subtotal += lineTotal;
        if (stack[h].sp) subEligible += lineTotal;   /* only sub-eligible lines get the sub discount */
        count += stack[h].qty;
        lines += 1;
      }
      var subSaving = (mode === 'sub') ? Math.round(subEligible * SUB_PCT / 100) : 0;
      var pct = stackPct(lines);
      var stackSaving = Math.round(subtotal * pct / 100);
      var mysteryValue = mystery ? (mystery.price || 0) : 0;
      var total = subtotal - subSaving - stackSaving;
      if (total < 0) total = 0;
      return {
        subtotal: subtotal, subSaving: subSaving, pct: pct, stackSaving: stackSaving,
        mysteryValue: mysteryValue, total: total,
        totalSavings: subSaving + stackSaving + mysteryValue, count: count, lines: lines
      };
    }

    function statusText(n, pct) {
      if (n <= 0) return 'Pick a product to start saving.';
      var off = pct ? (pct + '% off') : ('add 1 more for ' + TIER2_PCT + '% off');
      return n + ' of 4 picked · ' + off;
    }

    function reward(on, label) {
      return '<div class="bbb-reward' + (on ? ' on' : '') + '"><span class="bbb-reward__ic">' + ICON.check + '</span><span>' + escapeHtml(label) + '</span></div>';
    }
    function renderRewards(n) {
      if (!rewardsEl) return;
      rewardsEl.innerHTML =
        reward(n >= 2, TIER2_PCT + '% off your stack') +
        reward(n >= GIFT_UNLOCK, 'Free mystery gift') +
        reward(n >= 4, TIER4_PCT + '% off your stack');
    }

    function syncLadder(n) {
      if (!ladderRow) return;
      [].forEach.call(ladderRow.querySelectorAll('[data-tier]'), function (tEl) {
        var tn = parseInt(tEl.getAttribute('data-tier'), 10);
        tEl.classList.toggle('is-active', tn === n);
        tEl.classList.toggle('is-reached', tn <= n && n > 0);
      });
    }

    function plural(n) { return n + (n === 1 ? ' item' : ' items'); }

    /* ---- mystery gift panel + dice ---- */
    function giftCardHtml(g) {
      var img = g.image ? '<img src="' + escapeHtml(g.image) + '" alt="" width="56" height="56">' : '';
      return '<div class="bbb-gift">' + img + '<div class="bbb-gift__txt">' +
        (g.brand ? '<span class="bbb-gift__b">' + escapeHtml(g.brand) + '</span>' : '<span class="bbb-gift__b">Your gift</span>') +
        '<span class="bbb-gift__t">' + escapeHtml(g.title) + '</span>' +
        '<span class="bbb-gift__p"><s>' + money(g.price) + '</s> <span class="bbb-gift__free">Free</span></span>' +
        '</div></div>';
    }
    function mysteryStageHtml() {
      return '<div class="bbb-myst bbb-myst--unlocked" data-mystery-stage>' +
        '<div class="bbb-myst__head">' + ICON.gift + '<div><strong>Mystery gift unlocked</strong><span>A free surprise — roll the dice.</span></div></div>' +
        '<div class="bbb-dice-stage">' +
          '<span class="bbb-glow" aria-hidden="true"></span>' +
          '<div class="bbb-dice-wrap">' + diceHtml() + '</div>' +
          '<span class="bbb-dice-shadow" aria-hidden="true"></span>' +
        '</div>' +
        '<button type="button" class="bbb-myst__cta" data-roll><span>Roll for your gift</span></button>' +
        '<div class="bbb-myst__reveal" data-myst-reveal></div>' +
      '</div>';
    }
    function teardownDice() { if (diceR) { diceR.destroy(); diceR = null; } }
    function mountDice() {
      teardownDice();
      var cv = mysteryEl ? mysteryEl.querySelector('[data-dice]') : null;
      if (cv && cv.getContext) { diceR = createDiceRenderer(cv); diceR.init(); }
    }
    function renderMystery(n) {
      if (!mysteryEl) return;
      var state = (n < 1) ? 'hidden' : (n < GIFT_UNLOCK ? 'locked' : 'unlocked');
      /* unlocked panel is mounted once; don't rebuild (would reset the dice) on every pick */
      if (state === 'unlocked' && lastMysteryState === 'unlocked') return;
      lastMysteryState = state;
      if (state === 'hidden') { teardownDice(); mysteryEl.hidden = true; mysteryEl.innerHTML = ''; return; }
      mysteryEl.hidden = false;
      if (state === 'locked') {
        teardownDice();
        var remaining = GIFT_UNLOCK - n;
        mysteryEl.innerHTML = '<div class="bbb-myst bbb-myst--locked">' + ICON.lock +
          '<span>Add <b>' + remaining + '</b> more ' + (remaining === 1 ? 'pick' : 'picks') +
          ' to unlock a free mystery gift.</span></div>';
        return;
      }
      mysteryEl.innerHTML = mysteryStageHtml();
      mountDice();
    }

    var ROLL_MS = 2570;
    function rollDice() {
      if (rolling) return;
      var stage = mysteryEl ? mysteryEl.querySelector('[data-mystery-stage]') : null;
      if (!stage) return;
      if (!MYSTERY.length) {
        if (!stage.querySelector('.bbb-myst__note')) {
          stage.insertAdjacentHTML('beforeend', '<p class="bbb-myst__note">Connect a mystery-gift collection in the theme editor to enable prizes.</p>');
        }
        return;
      }
      rolling = true;
      [].forEach.call(stage.querySelectorAll('[data-roll]'), function (b) { b.disabled = true; });
      stage.classList.remove('is-rolling'); void stage.offsetWidth; stage.classList.add('is-rolling');
      var pick = MYSTERY[Math.floor(Math.random() * MYSTERY.length)];
      var done = false;
      function reveal() {
        if (done) return; done = true;
        if (!stage.isConnected) { rolling = false; return; }
        stage.classList.remove('is-rolling');
        mystery = { id: pick.id, title: pick.title, brand: pick.brand, price: pick.price, image: pick.image };
        var rev = stage.querySelector('[data-myst-reveal]');
        if (rev) rev.innerHTML = giftCardHtml(mystery) + '<button type="button" class="bbb-myst__again" data-roll>Roll again</button>';
        stage.classList.add('is-revealed');
        [].forEach.call(stage.querySelectorAll('[data-roll]'), function (b) { b.disabled = false; });
        rolling = false;
        render();
      }
      if (diceR) diceR.roll(reveal);
      window.setTimeout(reveal, ROLL_MS);
    }
    if (mysteryEl) {
      mysteryEl.addEventListener('click', function (e) { if (e.target.closest('[data-roll]')) rollDice(); });
    }

    /* ---- mystery-gift nudge: don't let the shopper walk away from a free gift ---- */
    function giftUnclaimed() { return pickCount() >= GIFT_UNLOCK && !mystery; }
    function syncGiftNudge(n) {
      if (!giftNudge) return;
      if (n >= GIFT_UNLOCK && !mystery) {
        giftNudge.hidden = false;
        if (giftNudgeMsg) giftNudgeMsg.textContent = "🎁 You've unlocked a free mystery gift — roll the dice to claim it before you check out.";
        if (giftSkipBtn) giftSkipBtn.hidden = true;   /* skip only offered once they try to proceed */
      } else {
        giftNudge.hidden = true;
        if (giftSkipBtn) giftSkipBtn.hidden = true;
        giftAck = false;                              /* reset so a fresh unlock nudges again */
      }
    }
    function goRoll() {
      /* on mobile the summary is a covering sheet — close it so the dice is visible */
      if (summary.classList.contains('is-open')) {
        closeSheet();
        window.setTimeout(function () {
          if (mysteryEl) { try { mysteryEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }
          rollDice();
        }, 260);
      } else {
        if (mysteryEl) { try { mysteryEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }
        rollDice();
      }
    }
    function promptGift() {
      if (!giftNudge) { giftAck = true; addStackToCart(); return; }
      giftNudge.hidden = false;
      if (giftNudgeMsg) giftNudgeMsg.textContent = "Hold on — you haven't claimed your free mystery gift. Roll the dice first, or add your stack without it.";
      if (giftSkipBtn) giftSkipBtn.hidden = false;
      giftNudge.classList.remove('bbb-flash'); void giftNudge.offsetWidth; giftNudge.classList.add('bbb-flash');
      try { giftNudge.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
    }
    if (giftRollBtn) giftRollBtn.addEventListener('click', goRoll);
    if (giftSkipBtn) giftSkipBtn.addEventListener('click', function () { giftAck = true; if (giftNudge) giftNudge.hidden = true; addStackToCart(); });

    /* ---- main render ---- */
    function render() {
      enforceTiers();
      var t = totals();

      itemsEl.innerHTML = '';
      var newKeys = {};
      Object.keys(stack).forEach(function (h) {
        var it = stack[h];
        newKeys[h] = 1;
        var li = document.createElement('li');
        li.className = 'bbb-sum__item' + (prevKeys[h] ? '' : ' bbb-sum__item--in');
        li.innerHTML =
          (it.img ? '<img src="' + escapeHtml(it.img) + '" alt="" loading="lazy" width="42" height="42">' : '') +
          '<div class="bbb-sum__item-main">' +
            '<div class="bbb-sum__item-name">' + escapeHtml(it.title) + '</div>' +
            '<div class="bbb-sum__item-price">' + money(it.price) + '</div>' +
          '</div>' +
          '<button type="button" class="bbb-sum__remove" data-remove aria-label="Remove ' + escapeHtml(it.title) + '">&times;</button>';
        li.querySelector('[data-remove]').addEventListener('click', function () { removeItem(h); });
        itemsEl.appendChild(li);
      });
      if (mystery) {
        newKeys.__mystery = 1;
        var gli = document.createElement('li');
        gli.className = 'bbb-sum__item bbb-sum__item--free' + (prevKeys.__mystery ? '' : ' bbb-sum__item--in');
        gli.innerHTML =
          (mystery.image ? '<img src="' + escapeHtml(mystery.image) + '" alt="" loading="lazy" width="42" height="42">' : '') +
          '<div class="bbb-sum__item-main">' +
            '<div class="bbb-sum__item-name">' + escapeHtml(mystery.title) + '</div>' +
            '<div class="bbb-sum__item-price"><s>' + money(mystery.price) + '</s> Free</div>' +
          '</div>' +
          '<span class="bbb-sum__free-tag">Gift</span>';
        itemsEl.appendChild(gli);
      }
      prevKeys = newKeys;

      emptyEl.hidden = t.lines > 0;
      itemsEl.hidden = t.lines === 0 && !mystery;
      countEl.textContent = plural(t.count);
      subtotalEl.textContent = money(t.subtotal);

      subsaveRow.hidden = !(t.subSaving > 0);
      if (subsaveLabel) subsaveLabel.textContent = 'Subscription saving (' + SUB_PCT + '%)';
      subsaveEl.textContent = '-' + money(t.subSaving);

      stacksaveRow.hidden = !(t.stackSaving > 0);
      if (stacksaveLabel) stacksaveLabel.textContent = 'Stack saving (' + t.pct + '%)';
      stacksaveEl.textContent = '-' + money(t.stackSaving);

      totalsaveRow.hidden = !(t.totalSavings > 0);
      totalsaveEl.textContent = '-' + money(t.totalSavings);

      totalEl.textContent = money(t.total);

      statusEl.textContent = statusText(t.lines, t.pct);
      renderRewards(t.lines);
      syncLadder(t.lines);
      renderMystery(t.lines);

      if (!ctaEl.classList.contains('is-loading')) {
        ctaEl.textContent = t.lines ? ('Add stack to cart — ' + money(t.total)) : 'Add stack to cart';
      }
      ctaEl.disabled = t.lines === 0;

      syncGiftNudge(t.lines);

      mobileBar.hidden = t.lines === 0;
      root.classList.toggle('bbb--bar-on', t.lines > 0);
      barCount.textContent = plural(t.count);
      barTotal.textContent = money(t.total);

      if (t.lines === 0 && summary.classList.contains('is-open')) closeSheet();
    }

    /* ---- filters (keep in-stack cards visible regardless of filter) ---- */
    function applyFilter(filter) {
      var anyVisible = false;
      cards.forEach(function (card) {
        var goals = card.getAttribute('data-goals') || '';
        var show = (filter === 'all') || (goals.indexOf(filter) !== -1) || card.classList.contains('is-instack');
        card.hidden = !show;
        if (show) anyVisible = true;
      });
      emptyGrid.hidden = anyVisible;
    }
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) { c.classList.remove('is-active'); c.setAttribute('aria-pressed', 'false'); });
        chip.classList.add('is-active'); chip.setAttribute('aria-pressed', 'true');
        applyFilter(chip.getAttribute('data-filter'));
      });
    });

    /* ---- card add (toggle, one of each) ---- */
    cards.forEach(function (card) {
      var handle = card.getAttribute('data-handle');
      var addBtn = card.querySelector('[data-add]');
      if (addBtn) addBtn.addEventListener('click', function () { toggleItem(handle, card); });
    });

    /* ---- subscription toggle ---- */
    function setMode(btn) {
      mode = btn.getAttribute('data-mode');
      modeBtns.forEach(function (b) { b.setAttribute('aria-checked', b === btn ? 'true' : 'false'); b.tabIndex = (b === btn ? 0 : -1); });
      render();
    }
    modeBtns.forEach(function (btn) { btn.addEventListener('click', function () { setMode(btn); }); });
    if (radiogroup) {
      radiogroup.addEventListener('keydown', function (e) {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
        e.preventDefault();
        var idx = modeBtns.indexOf(document.activeElement);
        if (idx === -1) idx = 0;
        var fwd = (e.key === 'ArrowRight' || e.key === 'ArrowDown');
        var next = (idx + (fwd ? 1 : -1) + modeBtns.length) % modeBtns.length;
        modeBtns[next].focus();
        setMode(modeBtns[next]);
      });
    }

    /* ---- mobile sheet (focus-trapped modal) ---- */
    var lastFocus = null;
    function setBgInert(on) {
      [main, mobileBar].forEach(function (el) {
        if (!el) return;
        if (on) { el.setAttribute('aria-hidden', 'true'); try { el.inert = true; } catch (e) {} }
        else { el.removeAttribute('aria-hidden'); try { el.inert = false; } catch (e) {} }
      });
    }
    function openSheet() {
      if (DESKTOP.matches) return;
      lastFocus = document.activeElement;
      summary.classList.add('is-open');
      scrim.classList.add('is-open');
      summary.setAttribute('role', 'dialog');
      summary.setAttribute('aria-modal', 'true');
      document.body.style.overflow = 'hidden';
      setBgInert(true);
      if (sheetClose) sheetClose.focus();
    }
    function closeSheet() {
      if (!summary.classList.contains('is-open')) return;
      summary.classList.remove('is-open');
      scrim.classList.remove('is-open');
      summary.removeAttribute('aria-modal');
      document.body.style.overflow = '';
      setBgInert(false);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    if (barOpen) barOpen.addEventListener('click', openSheet);
    if (sheetClose) sheetClose.addEventListener('click', closeSheet);
    if (scrim) scrim.addEventListener('click', closeSheet);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSheet(); });
    summary.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !summary.classList.contains('is-open')) return;
      var f = [].slice.call(summary.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    var onMq = function (e) { if (e.matches) closeSheet(); };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onMq);
    else if (DESKTOP.addListener) DESKTOP.addListener(onMq);

    /* ---- add to cart (subscription selling plan + mystery free line + tier-code redirect) ---- */
    function addStackToCart() {
      var t = totals();
      var n = t.lines;
      var items = Object.keys(stack).map(function (h) {
        var it = stack[h];
        var line = { id: parseInt(it.id, 10), quantity: it.qty, properties: { _Stack: 'Build a Bundle' } };
        if (mode === 'sub' && it.sp) line.selling_plan = parseInt(it.sp, 10);
        return line;
      }).filter(function (line) { return line.id && !isNaN(line.id); });
      if (!items.length) return;
      if (mystery && n >= GIFT_UNLOCK) {
        var mid = parseInt(mystery.id, 10);
        if (mid && !isNaN(mid)) items.push({ id: mid, quantity: 1, properties: { _Free: 'Mystery gift', _Stack: 'Build a Bundle' } });
      }
      if (errorEl) errorEl.hidden = true;
      ctaEl.classList.add('is-loading');
      ctaEl.disabled = true;
      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      }).then(function () {
        var code = (n >= 4) ? CODE_TIER4 : (n >= 2 ? CODE_TIER2 : '');
        var dest = routeRoot + 'cart';
        window.location.href = code
          ? (routeRoot + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(dest))
          : dest;
      }).catch(function () {
        ctaEl.classList.remove('is-loading');
        ctaEl.disabled = false;
        if (errorEl) { errorEl.textContent = "Couldn't add your stack. Please try again."; errorEl.hidden = false; }
      });
    }
    ctaEl.addEventListener('click', function () {
      if (giftUnclaimed() && !giftAck) { promptGift(); return; }
      addStackToCart();
    });

    /* ---- init ---- */
    modeBtns.forEach(function (b) { var on = b.getAttribute('data-mode') === mode; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
    render();
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-bbb]')).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
